import { useRef, useState } from 'react';
import { type Source, api, SHOTS, VOICES, type Brand, type Turn, INSET_POSITIONS, type InsetPosition, voiceLabel } from '../lib/api';
import { mmss } from '../lib/timeline';
import { PicturePicker } from './PicturePicker';

// ---------------------------------------------------------------------------
// The adjustments for whatever is selected.
//
// Every row is a property of the SCENE, and every row says what the value comes
// from as well as what it is — "from speech", "auto + taste", "silence". A
// creator looking at 4.8s deserves to know whether that is a measurement or a
// setting, because one of those is theirs to change and the other is not.
//
// Duration is the clearest case. On an ordinary scene it is the narration's
// measured length and there is nothing to drag; on an inserted scene it is a
// number the creator declared. Showing a slider on the first would be offering
// a control over a fact.
// ---------------------------------------------------------------------------

function Row({ label, from, value, children }: {
  label: string;
  /** Where the value comes from — a measurement, a default, or a choice. */
  from: string;
  value: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="insp-row">
      <span className="insp-label">{label}</span>
      <div className="insp-val">
        <span className="insp-from">{from}</span>
        <span className="insp-num">{value}</span>
      </div>
      {children}
    </div>
  );
}

export function Inspector({
  turn, index, total, seconds, measured, speakers, brand, backdropDim, onEdit, cardId, pictureKey, sources = [],
}: {
  turn: Turn | null;
  /** The card, for the graphic shot's pictures — chosen from its collection. */
  cardId?: string;
  /** Changes when the collection does, so the picker reloads. */
  pictureKey?: string;
  index: number | null;
  total: number;
  /** This scene's length, measured when a render produced it. */
  seconds: number;
  measured: boolean;
  speakers: string[];
  brand: Brand | null;
  /** The card's backdrop dimming, or null when it has no backdrop. */
  /** The card's backdrop dimming, or null when it has no backdrop. */
  backdropDim: number | null;
  onEdit: (patch: Partial<Turn>) => void;
  /** The ready sources, for the second camera's picker. */
  sources?: Source[];
}) {
  if (!turn || index === null) {
    return (
      <div className="insp">
        <div className="insp-hd">Nothing selected</div>
        <p className="hint">Pick a scene on the timeline to adjust it.</p>
      </div>
    );
  }

  const isTitle = turn.kind === 'title';
  const held = isTitle || (turn.holdSeconds !== undefined && !turn.text.trim());
  // A sync-sound scene is as long as its tape: nothing is read aloud, so
  // "speech" below is the span of the recording it plays.
  const sync = Boolean(turn.sourceId) && turn.audio === 'sync';
  const authored = turn.holdSeconds;
  // The narration's own length: the scene's measured seconds less the beat.
  // On a held scene there is no narration and this is the hold itself.
  const speech = Math.max(0, seconds - (turn.beat ?? 0.35));
  const shot = SHOTS.find((s) => s.id === (turn.shot ?? 'wide'));
  // What the renderer will actually draw. `single` is one panel however large
  // the cast is — saying "2 panels" beside a two-shot on a cast of one would be
  // describing a frame that cannot happen.
  const panels = turn.shot === 'single' ? 1
    : turn.shot === 'two-shot' ? Math.min(2, speakers.length)
    : Math.max(1, speakers.length);
  const voice = brand?.voices?.[turn.speaker];

  return (
    <div className="insp">
      <div className="insp-hd">
        Scene {index + 1} of {total} · {isTitle ? 'title — the show open' : held ? 'held' : shot?.label ?? 'wide'}
      </div>

      <Row label="Framing" from={shot?.label ?? 'wide'}
           value={held ? '—'
             : turn.shot === 'insert' ? (turn.sourceId ? 'face over footage' : 'needs footage')
             : turn.shot === 'graphic' ? (turn.graphic?.headline?.trim() || turn.graphic?.pictures.length ? 'host beside a graphic' : 'needs a headline or pictures')
             : `${panels} panel${panels === 1 ? '' : 's'}`}>
        <select
          value={turn.shot ?? 'wide'}
          // A held scene draws no panels at all, so framing it is choosing
          // between pictures that will not be drawn.
          disabled={held}
          title={held ? 'A held scene shows footage or a plain hold, not panels' : shot?.hint}
          onChange={(e) => onEdit({ shot: e.target.value as Turn['shot'] })}
        >
          {SHOTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </Row>

      {turn.shot === 'insert' && (
        <Row label="Face corner" from="over the footage" value={turn.inset?.position ?? 'bottom-right'}>
          <select value={turn.inset?.position ?? 'bottom-right'}
                  title="Which corner the speaker's face sits in"
                  onChange={(e) => onEdit({ inset: { position: e.target.value as InsetPosition } })}>
            {INSET_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Row>
      )}

      {turn.shot === 'graphic' && !held && (
        <GraphicRows turn={turn} cardId={cardId} logos={brand?.logos} onEdit={onEdit} refreshKey={pictureKey} />
      )}

      <Row label="Speaker" from={voice ? voiceLabel(voice) : 'default voice'}
           value={turn.speaker}>
        <select data-tool="speaker" value={turn.speaker}
                onChange={(e) => onEdit({ speaker: e.target.value })}>
          {(speakers.includes(turn.speaker) ? speakers : [turn.speaker, ...speakers])
            .map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Row>

      <Row label="Read it" from={voice?.startsWith('gemini:') ? 'this voice takes direction' : voice?.startsWith('venice:') ? 'a Venice voice reads plainly' : 'only a directed voice listens'}
           value={turn.read?.trim() ? 'as told' : 'plainly'}>
        <input type="text" maxLength={200} value={turn.read ?? ''}
               placeholder="warm and unhurried, a little amused"
               title="How to read this line, in words. A directed (gemini:) voice honours it; Venice and the other voices read plainly."
               onChange={(e) => onEdit({ read: e.target.value || undefined })} />
        <HearLine voice={voice} text={turn.text} read={turn.read} />
      </Row>

      {!held && cardId && (
        <Row label="Ask the director" from="this line only, in the speaker's voice" value={turn.text.trim() ? 'rewrite' : '—'}>
          <RewriteLine cardId={cardId} index={index} text={turn.text} onText={(text) => onEdit({ text })} />
        </Row>
      )}

      <Row
        label="Duration"
        // Which rule produced the number, because one of them is the creator's
        // to change and the other is not. A held scene's length is theirs. A
        // spoken scene's is max(speech, authored): a hold can EXTEND it past
        // the narration and never cut the narration short — so when the hold
        // is below the speech the row says the speech won, rather than showing
        // a number the render will not produce.
        from={held ? 'you set it'
          : sync
            ? (authored !== undefined && authored > speech
                ? `the footage, held to ${authored.toFixed(1)}s`
                : 'the footage: in to out' + (turn.sourceOut === undefined ? ' (the end)' : ''))
          : authored === undefined
            ? (measured ? 'from speech' : 'estimated') + (turn.sourceId ? ' · a hold lengthens the shot' : '')
          : authored > speech ? `speech, held to ${authored.toFixed(1)}s`
          : `speech wins (${authored.toFixed(1)}s asked)`}
        value={`${seconds.toFixed(1)}s`}
      >
        <input
          type="number" min={0.5} max={14400} step={0.5}
          value={authored ?? ''}
          placeholder={held ? '8' : 'hold for at least…'}
          title={held
            ? 'How long this scene runs'
            : 'Hold the shot at least this long. It can run past the line; it cannot cut it short.'}
          onChange={(e) => {
            // Blank means "no floor" on a spoken scene, and is refused on a
            // held one — a held scene with no length is a zero-second segment
            // ffmpeg drops without an error.
            if (e.target.value === '') {
              if (!held) onEdit({ holdSeconds: undefined });
              return;
            }
            onEdit({ holdSeconds: Number(e.target.value) });
          }}
        />
      </Row>

      {turn.sourceId && (
        // A take recorded on camera brings its own words. "Sync" plays the
        // footage's sound in this scene's slot and holds the picture for
        // exactly that long; the line's text becomes the caption. "Over" is
        // narration read across the footage, which is muted.
        <Row
          label="Sound"
          from={(turn.audio ?? 'over') === 'sync' ? 'A-roll' : 'B-roll'}
          value={(turn.audio ?? 'over') === 'sync' ? 'its own' : turn.audio === 'mixed' ? 'voice over it' : 'voiced'}
        >
          <select value={turn.audio ?? 'over'}
                  onChange={(e) => onEdit({ audio: e.target.value as Turn['audio'] })}>
            <option value="over">B-roll — narrate over it, the footage is muted</option>
            <option value="mixed">B-roll — narrate over its sound, the footage ducked under the voice</option>
            <option value="sync">A-roll — the take speaks, its own sound and length</option>
          </select>
        </Row>
      )}

      {turn.sourceId && turn.shot !== 'boxed' && (() => {
        // A second camera on the scene: the other angle of the same read in
        // the corner, or the two side by side. Picture only — the sound and
        // the length stay with the first source, which is why the picker
        // offers footage and never audio.
        const others = sources.filter((x) => x.id !== turn.sourceId && x.mode === 'stored' && x.status === 'ready' && (x.duration_ms ?? 0) > 0);
        const sec = turn.second ?? null;
        return (
          <Row label="Second camera" from={sec ? (sec.layout === 'split' ? 'split frame' : 'picture in picture') : 'one picture'}
               value={sec ? (sources.find((x) => x.id === sec.sourceId)?.name ?? 'a take').slice(0, 24) : 'none'}>
            <div className="row wrap" style={{ gap: 6 }} data-tool="second-camera">
              <select value={sec?.sourceId ?? ''} title="Another take on this scene — the other camera's angle"
                      onChange={(e) => onEdit({ second: e.target.value ? { layout: 'pip', ...(sec ?? {}), sourceId: e.target.value } : null })}>
                <option value="">none — one picture</option>
                {others.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              {sec && (
                <select value={sec.layout} title="In the corner, or side by side"
                        onChange={(e) => onEdit({ second: { ...sec, layout: e.target.value as 'pip' | 'split' } })}>
                  <option value="pip">picture in picture</option>
                  <option value="split">split frame</option>
                </select>
              )}
              {sec && sec.layout === 'pip' && (
                <select value={sec.position ?? 'bottom-right'} title="Which corner the second camera sits in"
                        onChange={(e) => onEdit({ second: { ...sec, position: e.target.value as InsetPosition } })}>
                  <option value="bottom-right">bottom right</option>
                  <option value="bottom-left">bottom left</option>
                  <option value="top-right">top right</option>
                  <option value="top-left">top left</option>
                </select>
              )}
              {sec && (
                <label className="sub-dim" title="Seconds into the second take to start from — line it up with the first">in{' '}
                  <input type="number" min={0} step={0.1} value={sec.sourceIn ?? 0} style={{ width: 64 }}
                         onChange={(e) => onEdit({ second: { ...sec, sourceIn: Math.max(0, Number(e.target.value) || 0) } })} />s
                </label>
              )}
            </div>
          </Row>
        );
      })()}

      {isTitle && (
        <Row label="Under the title" from="a small line" value={turn.sub ? 'set' : 'none'}>
          <input value={turn.sub ?? ''} maxLength={120} placeholder="coming up · with Niki and Spencer · 17 September"
                 onChange={(e) => onEdit({ sub: e.target.value || undefined })} />
        </Row>
      )}

      <Row label="Beat after" from="silence" value={`${(turn.beat ?? 0.35).toFixed(2)}s`}>
        <input
          type="range" min={0} max={2} step={0.05}
          value={turn.beat ?? 0.35}
          onChange={(e) => onEdit({ beat: Number(e.target.value) })}
        />
      </Row>

      {backdropDim !== null && (
        // Shown, not editable here, and the difference is the point: a backdrop
        // belongs to the CARD and every scene in it shares one. A slider in a
        // per-scene panel would imply each shot could have its own.
        //
        // "auto + taste" because the renderer measures what the image needs
        // against the region the captions occupy and this only darkens further
        // — it is taste on top of a measurement, not instead of one. The Mix
        // panel is where it is set, and the rail's backdrop tool goes there.
        <Row label="Backdrop dim" from="auto + taste · set in Mix"
             value={backdropDim.toFixed(2)} />
      )}
    </div>
  );
}

/**
 * The graphic shot's panel (F3): the headline stacked beside the host, the
 * host's side, and up to three pictures from THIS episode's collection —
 * the collection rather than the whole library, which is the point of the
 * Idea phase. Thumbs come from the cached small copies.
 */
function GraphicRows({ turn, cardId, logos, onEdit, refreshKey }: { turn: Turn; cardId?: string; logos?: string[] | null; onEdit: (patch: Partial<Turn>) => void; refreshKey?: string }) {
  const g = turn.graphic ?? { headline: '', pictures: [], side: 'left' as const };
  const set = (patch: Partial<NonNullable<Turn['graphic']>>) => onEdit({ graphic: { ...g, ...patch } });
  return (
    <>
      <Row label="Headline" from="stacked large beside the host" value={g.headline?.trim() ? 'set' : 'none'}>
        <input type="text" maxLength={60} value={g.headline ?? ''}
               placeholder="SCARE US IF YOU CAN"
               title="Six words or fewer read best. Set in caps, the first line in the accent."
               onChange={(e) => set({ headline: e.target.value })} />
      </Row>
      <Row label="Host sits" from="the graphic takes the other side" value={g.side}>
        <select value={g.side} onChange={(e) => set({ side: e.target.value as 'left' | 'right' })}>
          <option value="left">left</option>
          <option value="right">right</option>
        </select>
      </Row>
      <Row label="Pictures" from="from this episode's collection, three at most" value={`${g.pictures.length} of 3`}>
        <PicturePicker cardId={cardId} logos={logos} value={g.pictures} max={3} tool="graphic-pictures" refreshKey={refreshKey}
                       onChange={(pictures) => set({ pictures })} />
      </Row>
    </>
  );
}

/**
 * R8: rehearse the line. The speaker's cloud or directed voice says THIS
 * line, with its direction, before anything is rendered — a read-through
 * costs seconds, a render costs minutes. A local voice lives in the render
 * image and has no preview; the button says so rather than hiding.
 */
function HearLine({ voice, text, read }: { voice?: string; text: string; read?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  // Every voice can be heard now: cloud and directed voices from the API,
  // local ones from the preview service, which runs the render image.
  const cloud = Boolean(voice);
  async function hear() {
    if (!voice || !text.trim()) return;
    setBusy(true); setError(null);
    try {
      const url = await api.voiceSample(voice, read?.trim() || undefined, text);
      if (!url) { setError('a local voice has no preview'); return; }
      player.current?.pause();
      const a = new Audio(url); player.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
      <button type="button" className="btn tiny" disabled={busy || !cloud || !text.trim()}
              title={cloud ? 'Hear this line in the speaker\'s voice, read as told' : 'Give the speaker a voice in the Brand kit'}
              onClick={() => void hear()}>
        {busy ? 'Saying…' : '▶ Hear this line'}
      </button>
      {error && <span className="hint" style={{ color: 'var(--danger, #b91c1c)' }}>{error}</span>}
    </span>
  );
}

/**
 * D9: rewrite this line to an ask. The director returns the line; it goes
 * into the box as an unsaved edit, so keeping it is the same Save as any
 * other change and discarding it is closing without saving.
 */
function RewriteLine({ cardId, index, text, onText }: { cardId: string; index: number; text: string; onText: (text: string) => void }) {
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go(theAsk: string) {
    if (!theAsk.trim() || !text.trim()) return;
    setBusy(true); setError(null);
    try { onText(await api.rewriteLine(cardId, index, theAsk.trim())); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ display: 'flex', gap: 6 }}>
        <input type="text" maxLength={300} value={ask} placeholder="tighten it · make it a question · say the number"
               onChange={(e) => setAsk(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void go(ask); }}
               style={{ flex: 1, minWidth: 0 }} />
        <button type="button" className="btn tiny" disabled={busy || !ask.trim() || !text.trim()} onClick={() => void go(ask)}>
          {busy ? 'Rewriting…' : 'Rewrite'}
        </button>
      </span>
      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {['tighten it', 'make it a question', 'say the number', 'plainer words', 'warmer'].map((q) => (
          <button key={q} type="button" className="chip" disabled={busy || !text.trim()} onClick={() => { setAsk(q); void go(q); }}>{q}</button>
        ))}
      </span>
      {error && <span className="hint" style={{ color: 'var(--danger, #b91c1c)' }}>{error}</span>}
    </span>
  );
}
